<template>
  <v-container fluid>
    <!-- Case Header -->
    <v-row class="primary white--text">
      <v-col cols="11">
        <CaseHeader :selectedCase="selectedCase" />
      </v-col>
      <v-spacer />
      <v-col>
        <v-btn
          @click.stop="onClose"
          fab
          x-small
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-col>
    </v-row>
    <!-- Report Header -->
    <v-row>
      <v-col>
        <v-card>
          <v-card-title>
            Case Report
            <v-spacer />
            <span class="font-weight-black">
              <ResolutionStatusChips :resolutionStatus="selectedCaseReport.status" chips />
            </span>
          </v-card-title>

          <v-card-subtitle>
            <v-row no-gutters>
              <v-col> Created on {{ formatDateTime(localCaseReport.reportDate) }} </v-col>
              <v-col
                v-if="isResolved(localCaseReport.status)"
                align="right"
              >
                {{ getResolvedByString(localCaseReport.activity) }}
              </v-col>
            </v-row>
          </v-card-subtitle>
          <v-card-text>
            <!-- Status and assignee -->
            <v-row no-gutters>
              <v-spacer />
              <v-col
                cols="3"
                class="mt-4"
              >
                <v-select
                  dense
                  class="mt-4"
                  label="Assignee"
                  prepend-icon="mdi-account"
                  v-model="localCaseReport.assignee"
                  :items="getUsersWithUnassigned"
                  :item-text="
                    (item) => {
                      return getTextForUserSelect(item);
                    }
                  "
                  item-value="email"
                  @change="onSetAssignee"
                  :loading="assigneeLoading"
                  :disabled="!hasPermission('CAN_MODIFY_REPORTS')"
                />
                <v-select
                  dense
                  class="mt-4"
                  label="Status"
                  prepend-icon="mdi-check-all"
                  v-model="originalStatus"
                  :items="getReportStatuses"
                  item-text="status"
                  item-value="code"
                  @change="onSetStatus"
                  :loading="statusLoading"
                  :disabled="!hasPermission('CAN_MODIFY_REPORTS')"
                />
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <!-- Match Information -->
    <v-container fluid>
      <p class="text-h6 my-0">Match</p>
      <p class="text-h4 mb-2">{{ localCaseReport.match.sanction.name }}</p>
      <p
        class="text-subtitle-2"
        v-if="localCaseReport.match.sanction?.personDetails?.title"
      >
        {{ localCaseReport.match.sanction.personDetails.title }}
      </p>
      <v-row>
        <v-col cols="auto">
          <v-row v-if="localCaseReport.match.sanction.type">
            <v-col>
              <TypeIcon :type="localCaseReport.match.sanction.type" />
            </v-col>
          </v-row>
        </v-col>
        <v-col>
          <p class="subtitle-2">Source: {{ localCaseReport.match.sanction.source }}</p>
        </v-col>
      </v-row>
    </v-container>

    <MatchSummary
      :summary="localCaseReport.match.matchSummary"
      class="mt-6"
    />

    <TargetData
      :sanction="localCaseReport.match.sanction"
      class="mt-6"
    />

    <!-- Activity tab -->
    <v-sheet outlined class="mt-6">
      <v-container fluid>
        <h2>Activity</h2>
        <v-tabs v-model="activityTabs">
          <v-tab>Comments</v-tab>
          <v-tab>History</v-tab>
        </v-tabs>

        <v-tabs-items v-model="activityTabs">
          <!-- Comments -->
          <v-tab-item>
            <v-container fluid>
              <v-row align="center">
                <v-col cols="6">
                  <v-text-field
                    prepend-icon="mdi-message-reply-text"
                    @focus="showCommentButtons"
                    :placeholder="$t('component.caseReportComments.placeholder')"
                    v-model="newComment"
                  />
                </v-col>
                <v-col
                  cols="4"
                  v-show="commentButtonsVisible"
                >
                  <v-btn
                    small
                    color="primary"
                    :loading="commentButtonLoading"
                    class="white--text"
                    @click="onAddComment(newComment)"
                  >
                    {{ $t("label.save") }}
                  </v-btn>
                  <v-btn
                    small
                    color="primary"
                    text
                    @click="cancelComment"
                  >
                    {{ $t("label.cancel") }}
                  </v-btn>
                </v-col>
              </v-row>
              <v-row
                dense
                align="center"
              >
                <v-col>
                  <span
                    v-for="(comment, index) in localCaseReport.comments"
                    :key="'comment-' + index"
                  >
                    <v-row
                      dense
                      align="center"
                      class="mb-2"
                    >
                      <v-col cols="auto">
                        <v-avatar
                          color="purple"
                          class="mr-4"
                          size="30"
                        >
                          <span class="white--text">
                            {{ getInitials(comment.userName) }}
                          </span>
                        </v-avatar>
                      </v-col>
                      <v-col>
                        <b class="mr-4">{{ comment.userName }}</b>
                        <br />
                        <span class="caption">{{ formatDateTime(comment.date) }}</span>
                        <br />
                        {{ comment.comment }}
                      </v-col>
                      <v-spacer />
                    </v-row>
                  </span>
                </v-col>
              </v-row>
            </v-container>
          </v-tab-item>
          <!-- Other activity -->
          <v-tab-item>
            <v-container fluid>
              <v-row align="center">
                <v-col>
                  <span
                    v-for="(activity, index) in localCaseReport.activity"
                    :key="'activity-' + index"
                  >
                    <v-row
                      dense
                      align="center"
                      class="mb-2"
                    >
                      <v-col cols="auto">
                        <v-avatar
                          color="purple"
                          class="mr-4"
                          size="30"
                        >
                          <span class="white--text">
                            {{ getInitials(activity.userName) }}
                          </span>
                        </v-avatar>
                      </v-col>
                      <v-col>
                        <template v-if="activity.type === 'ASSIGN'">
                          <b>{{ activity.userName }} </b> changed the <strong>Assignee</strong>
                          <br />
                          <span class="text-body-2">{{ formatDateTime(activity.date) }}</span>
                          <br />
                          <span class="text-body-1">
                            <v-chip
                              label
                              outlined
                              small
                              class="mr-2"
                              >New Assignee</v-chip
                            >
                            →
                            {{ activity.assignedToUserName }}
                          </span>
                        </template>
                        <template v-else-if="activity.type === 'STATUS'">
                          <b>{{ activity.userName }} </b> changed the <strong>Status</strong>
                          <br />
                          <span class="text-body-2">{{ formatDateTime(activity.date) }}</span>
                          <br />
                          <span class="text-body-1">
                            <v-chip
                              label
                              outlined
                              small
                              class="mr-2"
                              >New Status</v-chip
                            >
                            →
                            <ResolutionStatusChips :resolutionStatus="activity.status" />
                          </span>
                        </template>
                      </v-col>
                    </v-row>
                  </span>
                </v-col>
              </v-row>
            </v-container>
          </v-tab-item>
        </v-tabs-items>
      </v-container>
    </v-sheet>
  </v-container>
</template>

<script>
import CaseHeader from "../components/case/CaseHeader";
import MatchSummary from "../components/case_report/MatchSummary";
import TargetData from "../components/case_report/TargetData.vue";
import TypeIcon from "../components/TypeIcon.vue";
import { formatDateTime } from "../utils/dateUtils";
import ResolutionStatusChips from "../components/ResolutionStatusChips";
import { mapGetters, mapActions } from "vuex";
import { isResolved, getResolvedByString } from "../utils/reportStatuses";
import { getTextForUserSelect, getInitials } from "../utils/userUtils";
import { hasPermission } from "../utils/permission";
import { addCommentApi, setStatusApi, setAssigneeApi } from "../api/caseReportApi";
export default {
  name: "CaseReport",
  components: {
    CaseHeader,
    ResolutionStatusChips,
    MatchSummary,
    TargetData,
    TypeIcon,
  },
  data() {
    return {
      localCaseReport: {},
      originalStatus: null,
      statusLoading: false,
      assigneeLoading: false,

      activityTabs: null,

      // Comment
      newComment: null,
      commentButtonsVisible: false,
      commentButtonLoading: false,
    };
  },
  props: {
    // The selected case prop is required because report embedded case records are note always sent back
    selectedCase: {
      type: Object,
      required: true,
    },
    selectedCaseReport: {
      type: Object,
      required: true,
    },
  },
  watch: {
    selectedCaseReport: {
      // This allows the component to handle changes without having to be destroyed.
      deep: true, // Watch for nested changes within the object
      immediate: true, // Run the handler immediately on component creation
      handler(newCaseReport) {
        this.localCaseReport = { ...newCaseReport }; // Shallow copy the object
        this.originalStatus = newCaseReport.status
      },
    },
  },
  computed: {
    ...mapGetters(["getCaseGroups", "users", "getReportStatuses"]),
    getUsersWithUnassigned: function () {
      var users = [...this.users];
      users.push({ email: null, name: "Unassigned" });
      return users;
    },
  },
  /**
   * 3 updates can be applied to a case report:
   *  1. Status set
   *  2. Assignee
   *  3. Comment added
   * All 3 updates are handled from within the
   * component (or API JS) and emitted back parent (CaseManager, Resolution)
   */
  methods: {
    ...mapActions(["getUsers", "setGlobalAlert"]),
    hasPermission,
    addCommentApi,
    setStatusApi,
    setAssigneeApi,
    formatDateTime,
    getTextForUserSelect,
    getInitials,
    isResolved,
    getResolvedByString,
    async onSetAssignee(assignee) {
      this.assigneeLoading = true;
      try {
        const updatedReport = await this.setAssigneeApi(this.selectedCaseReport.id, assignee);
        // Emit the updated case report
        this.$emit("caseReportUpdated", updatedReport);
      } catch (error) {
        this.setGlobalAlert({
          alert: true,
          color: "red",
          alertTitle: "Oops.",
          alertMessage: "Unable to set report assignee, please try again.",
        });
      } finally {
        this.assigneeLoading = false;
      }
    },
    async onSetStatus(status) {
      this.statusLoading = true;
      try {
        const updatedReport = await this.setStatusApi(this.selectedCaseReport.id, status);
        // Emit the updated case report
        this.$emit("caseReportUpdated", updatedReport);
      } catch (error) {
        this.originalStatus = this.selectedCaseReport.status
        this.setGlobalAlert({
          alert: true,
          color: "red",
          alertTitle: "Oops.",
          alertMessage: "Unable to set report status, please try again.",
        });
      } finally {
        this.statusLoading = false;
      }
    },
    async onAddComment(comment) {
      this.commentButtonLoading = true;
      try {
        const updatedReport = await this.addCommentApi(this.selectedCaseReport.id, comment);
        // Emit the updated case report
        this.$emit("caseReportUpdated", updatedReport);
      } catch (error) {
        this.setGlobalAlert({
          alert: true,
          color: "red",
          alertTitle: "Oops.",
          alertMessage: "Unable to add the comment, please try again.",
        });
      } finally {
        this.commentButtonLoading = false;
        this.newComment = null;
      }
    },
    cancelComment() {
      this.commentButtonsVisible = false;
      this.newComment = null;
    },
    showCommentButtons() {
      this.commentButtonsVisible = true;
    },
    onClose() {
      this.$emit("onClose");
    },
  },
};
</script>
